import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import * as classNames from 'classnames';
import ReportsListTable from './ReportsListTable';
import { ReportAdministrationView, ReportOpeningState } from './ReportsState';
import { Query, Report, Permission, SSRSCredential } from '../misc/api/apiTypes';
import * as AdministrationActions from './Administration/AdministrationActions';
import { Folder } from '../misc/api/ReportsApiTypes';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import { ClearAllFiltersAction, InitializeReportsAsyncAction, InitializeNameFilterAsyncAction, ToggleReportModalAction } from './ReportActions';
import { GetCredentialsAsyncAction, TestConnectionAsyncAction, GetReportRolesAsyncAction, GetSelectedRootFolderAction } from './Administration/AdministrationActions';
import ReportServerConnectionModal from './Administration/ReportServerConnectionModal';
import { Authorization } from '../misc/components/Authorization';
import LoadingWithMessage from '../misc/components/LoadingWithMessage';
import { MainState } from "../Core/reducers";
import { BaseReportsWizard } from './BaseReportsWizard/BaseReportsWizard';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReportModal from './Administration/ReportModal';
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../Core/SecurityContext';

export interface ReportsAppProps {
    listTableQuery: Query<Report>;
    reportOpening: ReportOpeningState;

    isConnectedToReportServer: boolean;
    isImportBaseReportsVisible: boolean;

    availableFolders: Array<Folder>;
    currentAdministrationView: ReportAdministrationView;
    defaultCredentials: SSRSCredential;
    dispatch?: Dispatch<MainState>;
    selectedStudyRootFolder?: string;
    showReportModal: boolean;
    selectedReportId?: string;
}

export interface ReportsAppState {
    showReportModal: boolean;
}

export const ReportsApp = (props: ReportsAppProps) => {

    const { study } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        props.dispatch(GetCredentialsAsyncAction.create(legacyClient));
        props.dispatch(TestConnectionAsyncAction.create(legacyClient));
        props.dispatch(InitializeNameFilterAsyncAction.create(legacyClient));
        props.dispatch(GetReportRolesAsyncAction.create(client.client));
        props.dispatch(GetSelectedRootFolderAction.create(legacyClient));
    }, []);

    const renderListTable = () => {
        if (props.isConnectedToReportServer) {
            return <ReportsListTable />;
        } else if (props.isConnectedToReportServer === null) {
            return (<LoadingWithMessage message="Connecting to Report Server..." />);
        } else {
            return <Alert show={true} category={AlertCategoryEnum.Danger}>There is no connection to the Report Server</Alert>;
        }
    }

    const clearAllReportsFilters = () => {
        props.dispatch(ClearAllFiltersAction.create());
        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const onReportServerConnectionClick = () => {
        props.dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.Credentials));
        props.dispatch(AdministrationActions.ToggleReportServerConnectionVisibilityAction.create());
    }

    const onImportBaseReportsClick = () => {
        props.dispatch(AdministrationActions.ToggleImportBaseReportsModalVisibilityAction.create());
        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const onSelectStudyFolderClick = () => {
        props.dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.RootFolder));
        props.dispatch(AdministrationActions.ToggleReportServerConnectionVisibilityAction.create());
    }

    const toggleReportModal = () => {
        if (props.showReportModal)
            props.dispatch(InitializeReportsAsyncAction.create(legacyClient));

        props.dispatch(ToggleReportModalAction.create());
    }

    const connectionStatusClasses = classNames("fas", "fa-circle", props.isConnectedToReportServer ? "text-success" : "text-danger");

    const isConnectedTooltip = (<Tooltip id="descriptionTooltip" style={{ zIndex: 2500 }}>
        {props.isConnectedToReportServer ? "Connected" : "Cannot connect to Report Server"}
    </Tooltip>);

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className='text-primary'>Reports</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <OverlayTrigger placement="top" overlay={isConnectedTooltip}>
                                <button className="btn btn-primary" onClick={() => onReportServerConnectionClick()} data-automationid="report-server-connection-button">
                                    Report Server Connection
                                    &nbsp;
                                    <i className={connectionStatusClasses}></i>
                                </button>
                            </OverlayTrigger>&nbsp;
                            <button className="btn btn-primary" disabled={!props.isConnectedToReportServer} onClick={() => onSelectStudyFolderClick()} data-automationid="select-study-button">Select Study Folder</button>&nbsp;
                        </Authorization>
                        <Authorization permissionId={PermissionEnum.ImportBaseReports} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <button className="btn btn-primary" onClick={() => onImportBaseReportsClick()}>Import Base Reports</button>&nbsp;
                        </Authorization>
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <button className="btn btn-primary" disabled={!props.isConnectedToReportServer || !props.selectedStudyRootFolder} onClick={toggleReportModal} data-automationid="add-report-button">Add Report</button>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={() => clearAllReportsFilters()}>Clear All Filters</button>
                    </div>
                    <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                        <ReportServerConnectionModal />
                    </Authorization>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            {renderListTable()}
                        </div>
                    </div>
                </div>
            </div>
            <Authorization permissionId={PermissionEnum.ImportBaseReports} studyId={study.id} currentPermissions={currentUserPermissions}>
                {props.isImportBaseReportsVisible ? <BaseReportsWizard onClose={onImportBaseReportsClick} /> : null}
            </Authorization>
            {props.showReportModal ? <ReportModal definedReportId={props.selectedReportId} show={props.showReportModal} onCloseClick={toggleReportModal} /> : null}
        </>
    );
}

function mapStateToProps(state: MainState): ReportsAppProps {
    return {
        listTableQuery: state.reports.listTable.reportsQuery,
        reportOpening: state.reports.listTable.reportOpening,

        isImportBaseReportsVisible: state.reports.administration.isImportBaseReportsVisible,
        isConnectedToReportServer: state.reports.administration.isConnectedToReportServer,
        defaultCredentials: state.reports.administration.credentials,
        availableFolders: state.reports.administration.availableFolders ? state.reports.administration.availableFolders.Data : null,
        currentAdministrationView: state.reports.administration.currentView,
        selectedStudyRootFolder: state.reports.administration.selectedStudyRootFolder,
        showReportModal: state.reports.listTable.showReportModal,
        selectedReportId: state.reports.listTable.selectedReportId
    }
}

export default connect<ReportsAppProps, any, void>(mapStateToProps)(ReportsApp);
